import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";

export const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value]);

    return debouncedValue;
};

export const debounceFunc = (func, delay) => {
    let timerId;
    return (...args) => {
        clearTimeout(timerId);
        timerId = setTimeout(() => func(...args), delay);
    };
};

export const useDebounceFunc = (callback, delay) => {
    const timer = useRef();

    const debounceCallback = useCallback(
        (...args) => {
            if (timer.current) {
                clearInterval(timer.current);
            }

            timer.current = setTimeout(() => {
                callback(...args);
            }, delay);
        },
        [callback, delay],
    );

    return debounceCallback;
};

export const useClickOutside = (val, ref, cb) => {
    const handleClick = (e) => {
        //чтоб при клике на тост, куки не вырубалась модалка
        const excludes = [
            ".p-toast",
            ".cookies",
            ".p-sidebar-mask",
            ".p-datepicker",
            ".p-multiselect-panel",
            ".p-dropdown-panel",
            ".p-sidebar",
            ".p-dialog-mask",
            ".p-image-mask",
            ".tox-toolbar__overflow",
            ".tox-menu",
            ".tox-dialog-wrap",
            ".container",
            ".rates_block",
            ".p-treenode .p-treenode-selectable:has(.p-treenode-label.tree-node-only-toggle)",
        ];

        if (e.target.tagName == "BODY") return; //чтобы календарь открытый и выехавший за пределы блока не вызывал попап черновика

        for (let i = 0; i < excludes.length; i++) {
            const cl = excludes[i];
            const el = document.querySelectorAll(cl);

            for (let index = 0; index < el.length; index++) {
                const element = el[index];
                if (element && element.contains(e.target)) return;
            }
        }
        //чтоб при клике на тост, куки не вырубалась модалка
        if (ref.current && !ref.current.contains(e.target)) {
            cb(e);
        }
    };

    useEffect(() => {
        if (val) {
            document.addEventListener("click", handleClick, { capture: true });
        } else {
            document.removeEventListener("click", handleClick, { capture: true });
        }

        return () => {
            document.removeEventListener("click", handleClick, { capture: true });
        };
    });
};

export const useGetHeightToScrollContainer = (id, cb) => {
    useEffect(() => {
        resizeFunc();
        window.addEventListener("resize", resizeFunc);

        return () => window.removeEventListener("resize", resizeFunc);
    }, []);

    const resizeFunc = () => {
        const mainContainer = document.getElementById("offset-container");
        const elem = document.getElementById(id);
        if (elem && mainContainer) cb(mainContainer.clientHeight + mainContainer.offsetTop - elem.getBoundingClientRect().top);
    };
};

export const useHandleResize = () => {
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const handleResize = () => {
        setIsMobile(window.innerWidth <= 820);
    };

    return isMobile;
};

export const useInViewport = (elems = [], cb, isStart) => {
    useEffect(() => {
        if (isStart) return;

        const debouncedScrollHandler = debounceFunc(scrollHandler, 2000);

        setTimeout(() => {
            debouncedScrollHandler();
            document.addEventListener("scroll", debouncedScrollHandler);

            return () => {
                document.removeEventListener("scroll", debouncedScrollHandler);
            };
        }, 40);
    }, [isStart]);

    let tmpArr = [];

    const scrollHandler = (e) => {
        const screenHeight = document.documentElement.clientHeight;
        const nodeArr = elems
            .reduce((acc, row) => {
                const node = document.querySelectorAll(row);
                acc.push([...node]);
                return acc;
            }, [])
            .flat();

        nodeArr.forEach((node) => {
            const rect = node.getBoundingClientRect();
            if (rect.top >= 0 && rect.bottom <= screenHeight) {
                if (!tmpArr.some((s) => s.text == node.id)) {
                    cb(node);
                    tmpArr.push({ node, text: node.id });
                }
            } else {
                tmpArr = tmpArr.filter((f) => f.text !== node.id);
            }
        });
    };
};

export const useNavigationHandler = () => {
    const navigate = useNavigate();

    return (e, path, options = {}) => {
        const fullUrl = `${window.location.origin}${path}`;

        if (e?.button === 1 || e?.ctrlKey || e?.metaKey) {
            window.open(fullUrl, "_blank");
        } else {
            navigate(path, { ...options });
        }
    };
};
